import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';

export default function ProductDetail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const productId = searchParams.get('id');
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [cartCount, setCartCount] = useState(0);
  const [showWishlistNotification, setShowWishlistNotification] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    const updateCartCount = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
        setCartCount(count);
      }
    };
    
    updateCartCount();
  }, []);

  const addToCart = () => {
    const savedCart = localStorage.getItem('cart');
    const cart = savedCart ? JSON.parse(savedCart) : [];
    
    const existingItem = cart.find((item: any) => item.id === product.id);
    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      cart.push({
        id: product.id,
        name: product.name,
        category: product.category,
        price: product.price,
        image: product.images[0],
        quantity: quantity,
        prescriptionRequired: product.prescriptionRequired
      });
    }
    
    localStorage.setItem('cart', JSON.stringify(cart));
    setCartCount(cart.reduce((sum: number, item: any) => sum + item.quantity, 0));
    navigate('/cart');
  };

  const addToWishlist = () => {
    const savedWishlist = localStorage.getItem('wishlist');
    const wishlist = savedWishlist ? JSON.parse(savedWishlist) : [];
    
    const exists = wishlist.find((item: any) => item.id === product.id);
    if (!exists) {
      wishlist.push({
        id: product.id,
        name: product.name,
        category: product.category,
        price: product.price,
        image: product.images[0]
      });
      localStorage.setItem('wishlist', JSON.stringify(wishlist));
      setShowWishlistNotification(true);
      setTimeout(() => setShowWishlistNotification(false), 3000);
    }
  };

  const shareProduct = (platform: string) => {
    const url = window.location.href;
    const text = `Check out ${product.name} on MediCare`;
    
    switch(platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
        break;
    }
    setShowShareMenu(false);
  };

  const products = [
    {
      id: 1,
      name: 'Orlistat 120mg',
      category: 'Weight Loss',
      price: 125.99,
      originalPrice: 159.99,
      images: [
        'https://public.readdy.ai/ai/img_res/392dd86e03b9d309c3d496353ad25fe0.jpg',
        'https://readdy.ai/api/search-image?query=orlistat%20120mg%20capsules%20blister%20pack%20detailed%20view%20professional%20pharmaceutical%20photography%20clean%20white%20background%20medical%20product%20shot&width=600&height=600&seq=102&orientation=squarish',
        'https://readdy.ai/api/search-image?query=orlistat%20weight%20loss%20medication%20bottle%20label%20information%20professional%20pharmaceutical%20packaging%20photography%20clean%20background&width=600&height=600&seq=103&orientation=squarish'
      ],
      rating: 4.5,
      reviewCount: 342,
      inStock: true,
      stockCount: 25,
      description: 'Orlistat 120mg is a prescription weight-loss medication that works by blocking about one-third of the fat in the foods you eat from being absorbed. It is used together with a reduced-calorie diet to help significantly overweight people lose weight and maintain weight loss.',
      dosage: 'Take one 120mg capsule with each main meal that contains fat (up to 3 capsules daily).',
      sideEffects: 'Common side effects may include stomach pain, gas with discharge, urgent need to have a bowel movement, and fatty or oily stools.',
      contraindications: 'Do not use if you have chronic malabsorption syndrome, cholestasis, or if you are allergic to orlistat.',
      manufacturer: 'Roche Pharmaceuticals',
      activeIngredient: 'Orlistat 120mg',
      prescriptionRequired: true
    },
    {
      id: 2,
      name: 'Amoxicillin 500mg',
      category: 'Antibiotics',
      price: 125.99,
      originalPrice: 159.99,
      images: [
        'https://readdy.ai/api/search-image?query=professional%20amoxicillin%20antibiotic%20capsules%20in%20medical%20blister%20pack%20on%20clean%20white%20background%20pharmaceutical%20product%20photography%20soft%20lighting%20high%20quality%20medical%20shot&width=600&height=600&seq=201&orientation=squarish',
        'https://readdy.ai/api/search-image?query=amoxicillin%20500mg%20capsules%20detailed%20view%20professional%20pharmaceutical%20photography%20clean%20white%20surface%20medical%20product%20shot&width=600&height=600&seq=202&orientation=squarish'
      ],
      rating: 4.8,
      reviewCount: 156,
      inStock: true,
      stockCount: 42,
      description: 'Amoxicillin is a penicillin antibiotic that fights bacteria in your body. It is used to treat many different types of infection caused by bacteria, such as tonsillitis, bronchitis, pneumonia, and infections of the ear, nose, throat, skin, or urinary tract.',
      dosage: 'Take 500mg every 8 hours or as directed by your physician.',
      sideEffects: 'Common side effects include nausea, vomiting, diarrhea, and stomach pain.',
      contraindications: 'Do not use if allergic to penicillin or amoxicillin.',
      manufacturer: 'GlaxoSmithKline',
      activeIngredient: 'Amoxicillin 500mg',
      prescriptionRequired: true
    },
    {
      id: 3,
      name: 'Ibuprofen 400mg',
      category: 'Pain Relief',
      price: 125.99,
      originalPrice: 159.99,
      images: [
        'https://readdy.ai/api/search-image?query=ibuprofen%20pain%20relief%20tablets%20in%20modern%20pharmaceutical%20packaging%20on%20clean%20white%20surface%20professional%20medical%20product%20photography%20soft%20lighting%20high%20quality%20shot&width=600&height=600&seq=301&orientation=squarish',
        'https://readdy.ai/api/search-image?query=ibuprofen%20400mg%20tablets%20blister%20pack%20detailed%20view%20professional%20pharmaceutical%20photography%20clean%20background&width=600&height=600&seq=302&orientation=squarish'
      ],
      rating: 4.7,
      reviewCount: 289,
      inStock: true,
      stockCount: 78,
      description: 'Ibuprofen is a nonsteroidal anti-inflammatory drug (NSAID) used to reduce fever and treat pain or inflammation caused by many conditions such as headache, toothache, back pain, arthritis, menstrual cramps, or minor injury.',
      dosage: 'Take 400mg every 4-6 hours as needed. Do not exceed 1200mg in 24 hours.',
      sideEffects: 'May cause stomach upset, heartburn, dizziness, or headache.',
      contraindications: 'Do not use if you have a history of stomach ulcers or kidney problems.',
      manufacturer: 'Pfizer',
      activeIngredient: 'Ibuprofen 400mg',
      prescriptionRequired: false
    }
  ];

  const product = products.find(p => p.id === parseInt(productId || '1')) || products[0];

  return (
    <div className="min-h-screen bg-white">
      {/* Wishlist Notification */}
      {showWishlistNotification && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2">
          <i className="ri-heart-fill"></i>
          <span>Added to wishlist!</span>
        </div>
      )}

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>MediCare</Link>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
              <a href="/#categories" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Categories</a>
              <a href="/#products" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Products</a>
              <a href="#about" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">About</a>
              <a href="#contact" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
            </div>
            <div className="flex items-center space-x-4">
              <button className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                <i className="ri-search-line text-xl"></i>
              </button>
              <Link to="/cart" className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer relative">
                <i className="ri-shopping-cart-line text-xl"></i>
                <span className="absolute -top-1 -right-1 bg-teal-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cartCount}</span>
              </Link>
              <Link to="/auth" className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                <i className="ri-user-line text-xl"></i>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="flex text-sm text-gray-500">
          <Link to="/" className="hover:text-teal-600 cursor-pointer">Home</Link>
          <span className="mx-2">/</span>
          <span className="hover:text-teal-600 cursor-pointer">{product.category}</span>
          <span className="mx-2">/</span>
          <span className="text-gray-900">{product.name}</span>
        </nav>
      </div>

      {/* Product Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div>
            <div className="mb-4">
              <img 
                src={product.images[selectedImage]} 
                alt={product.name} 
                className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              {product.images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`${product.name} ${index + 1}`}
                  className={`h-24 object-cover object-top rounded-lg cursor-pointer border-2 ${
                    selectedImage === index ? 'border-teal-600' : 'border-gray-200'
                  } hover:border-teal-400 transition-colors`}
                  onClick={() => setSelectedImage(index)}
                />
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <div className="mb-4">
              <span className="text-sm text-teal-600 font-semibold">{product.category}</span>
              <h1 className="text-3xl font-bold text-gray-900 mt-2">{product.name}</h1>
            </div>

            <div className="flex items-center mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i
                    key={i}
                    className={`${
                      i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'
                    } text-yellow-400 text-lg`}
                  ></i>
                ))}
              </div>
              <span className="ml-2 text-gray-600">({product.reviewCount} reviews)</span>
            </div>

            <div className="mb-6">
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-gray-900">${product.price}</span>
                {product.originalPrice && (
                  <span className="text-xl text-gray-500 line-through">${product.originalPrice}</span>
                )}
              </div>
              {product.inStock ? (
                <p className="text-green-600 font-semibold mt-2">
                  <i className="ri-checkbox-circle-line mr-1"></i>
                  In Stock ({product.stockCount} available)
                </p>
              ) : (
                <p className="text-red-600 font-semibold mt-2">
                  <i className="ri-close-circle-line mr-1"></i>
                  Out of Stock
                </p>
              )}
            </div>

            {product.prescriptionRequired && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <i className="ri-file-text-line text-yellow-600 text-lg mr-2"></i>
                  <span className="text-yellow-800 font-semibold">Prescription Required</span>
                </div>
                <p className="text-yellow-700 text-sm mt-1">This medication requires a valid prescription from a licensed healthcare provider.</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity</label>
              <div className="flex items-center space-x-4">
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-12 text-center font-semibold">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(Math.min(product.stockCount, quantity + 1))}
                    className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <button 
                  onClick={addToCart}
                  className="flex-1 bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer font-semibold"
                >
                  <i className="ri-shopping-cart-line mr-2"></i>
                  Add to Cart - ${(product.price * quantity).toFixed(2)}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <button 
                onClick={addToWishlist}
                className="border-2 border-teal-600 text-teal-600 px-6 py-3 rounded-lg hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer font-semibold"
              >
                <i className="ri-heart-line mr-2"></i>
                Add to Wishlist
              </button>
              <div className="relative">
                <button 
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="w-full border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer font-semibold"
                >
                  <i className="ri-share-line mr-2"></i>
                  Share Product
                </button>
                {showShareMenu && (
                  <div className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-10 w-48">
                    <button 
                      onClick={() => shareProduct('facebook')}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded flex items-center space-x-2 cursor-pointer"
                    >
                      <i className="ri-facebook-fill text-blue-600"></i>
                      <span>Facebook</span>
                    </button>
                    <button 
                      onClick={() => shareProduct('twitter')}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded flex items-center space-x-2 cursor-pointer"
                    >
                      <i className="ri-twitter-fill text-blue-400"></i>
                      <span>Twitter</span>
                    </button>
                    <button 
                      onClick={() => shareProduct('whatsapp')}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded flex items-center space-x-2 cursor-pointer"
                    >
                      <i className="ri-whatsapp-fill text-green-600"></i>
                      <span>WhatsApp</span>
                    </button>
                    <button 
                      onClick={() => shareProduct('copy')}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded flex items-center space-x-2 cursor-pointer"
                    >
                      <i className="ri-file-copy-line text-gray-600"></i>
                      <span>Copy Link</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t pt-6">
              <p className="text-sm text-gray-600"><strong>SKU:</strong> MED{product.id.toString().padStart(6, '0')}</p>
            </div>
          </div>
        </div>

        {/* Product Tabs */}
        <div className="mt-16">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {['description', 'dosage', 'sideEffects', 'reviews'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                    activeTab === tab
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab === 'description' && 'Description'}
                  {tab === 'dosage' && 'Dosage & Usage'}
                  {tab === 'sideEffects' && 'Side Effects'}
                  {tab === 'reviews' && 'Reviews'}
                </button>
              ))}
            </nav>
          </div>

          <div className="py-8">
            {activeTab === 'description' && (
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed">{product.description}</p>
                {product.contraindications && (
                  <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="font-semibold text-red-800 mb-2">Contraindications</h4>
                    <p className="text-red-700">{product.contraindications}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'dosage' && (
              <div className="prose max-w-none">
                <h4 className="font-semibold text-gray-900 mb-4">Recommended Dosage</h4>
                <p className="text-gray-700 leading-relaxed">{product.dosage}</p>
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h5 className="font-semibold text-blue-800 mb-2">Important Note</h5>
                  <p className="text-blue-700">Always follow your healthcare provider's instructions. Do not exceed the recommended dose without consulting a medical professional.</p>
                </div>
              </div>
            )}

            {activeTab === 'sideEffects' && (
              <div className="prose max-w-none">
                <h4 className="font-semibold text-gray-900 mb-4">Possible Side Effects</h4>
                <p className="text-gray-700 leading-relaxed">{product.sideEffects}</p>
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h5 className="font-semibold text-yellow-800 mb-2">When to Seek Medical Help</h5>
                  <p className="text-yellow-700">Contact your healthcare provider immediately if you experience severe or persistent side effects, allergic reactions, or any unexpected symptoms.</p>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <div className="mb-8">
                  <h4 className="font-semibold text-gray-900 mb-4">Customer Reviews</h4>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="flex items-center">
                      <span className="text-4xl font-bold text-gray-900">{product.rating}</span>
                      <div className="ml-2">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <i
                              key={i}
                              className={`${
                                i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'
                              } text-yellow-400 text-lg`}
                            ></i>
                          ))}
                        </div>
                        <p className="text-sm text-gray-600">{product.reviewCount} reviews</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="border-b border-gray-200 pb-6">
                    <div className="flex items-center mb-2">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
                        ))}
                      </div>
                      <span className="ml-2 font-semibold">Sarah M.</span>
                      <span className="ml-2 text-sm text-gray-500">Verified Purchase</span>
                    </div>
                    <p className="text-gray-700">"This medication has been very effective for my weight loss journey. Noticed results within the first month. Highly recommend following the dietary guidelines."</p>
                  </div>

                  <div className="border-b border-gray-200 pb-6">
                    <div className="flex items-center mb-2">
                      <div className="flex items-center">
                        {[...Array(4)].map((_, i) => (
                          <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
                        ))}
                        <i className="ri-star-line text-yellow-400 text-sm"></i>
                      </div>
                      <span className="ml-2 font-semibold">Michael R.</span>
                      <span className="ml-2 text-sm text-gray-500">Verified Purchase</span>
                    </div>
                    <p className="text-gray-700">"Good product, fast delivery. Had some minor side effects initially but they subsided after a few days. Overall satisfied with the results."</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Related Products</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4).map((relatedProduct) => (
              <Link
                key={relatedProduct.id}
                to={`/product?id=${relatedProduct.id}`}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all group cursor-pointer"
              >
                <div className="relative overflow-hidden">
                  <img src={relatedProduct.images[0]} alt={relatedProduct.name} className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-4">
                  <p className="text-sm text-teal-600 font-semibold mb-1">{relatedProduct.category}</p>
                  <h4 className="font-bold text-gray-900 mb-2">{relatedProduct.name}</h4>
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-bold text-gray-900">${relatedProduct.price}</p>
                    <div className="flex items-center">
                      <i className="ri-star-fill text-yellow-400 text-sm"></i>
                      <span className="ml-1 text-sm text-gray-600">{relatedProduct.rating}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: '"Pacifico", serif' }}>MediCare</h3>
              <p className="text-gray-400 mb-4">Your trusted partner in healthcare, delivering quality medicines to your doorstep.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-teal-400 transition-colors cursor-pointer">Home</Link></li>
                <li><a href="#about" className="hover:text-teal-400 transition-colors cursor-pointer">About Us</a></li>
                <li><a href="#contact" className="hover:text-teal-400 transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Categories</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-teal-400 transition-colors cursor-pointer">Weight Loss</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors cursor-pointer">Antibiotics</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors cursor-pointer">Pain Relief</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-phone-line mt-1 mr-2"></i>
                  <span>+1 (929) 268-8770</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-mail-line mt-1 mr-2"></i>
                  <span>sam4104t@gmail.com</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">&copy; 2024 MediCare. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-teal-400 transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
